import { AppSignalClient } from 'services/appSignal';
import { ZmyleErrorId } from 'utils/constants';
import { isAppRateLimiterResponse, isAxiosError, isZmyleError } from './types';
import {
  shouldFilterError,
  getErrorNameByErrorId,
  buildErrorInstanceFromZmyleError,
} from './error';

interface CaptureExceptionParams {
  error: unknown;
  tags?: Record<string, string>;
  metadata?: Record<string, any>;
}

export const captureException = async (params: CaptureExceptionParams) => {
  const { error, metadata, tags } = params;

  const appSignal = AppSignalClient.getInstance();

  if (await shouldFilterError(error)) return;

  if (isAppRateLimiterResponse(error)) {
    const isRateLimitedByIP =
      error?.rateLimiter.clientIP?.remainingPoints === 0;
    const isRateLimitedByGiftcardId =
      error?.rateLimiter.giftcardId?.remainingPoints === 0;

    const errorName = getErrorNameByErrorId(ZmyleErrorId.TOO_MANY_REQUESTS);

    let groupBy = '';
    if (isRateLimitedByIP) {
      const contextId = metadata?.clientIP || '??.??.??.??';
      groupBy = `IP: ${contextId}`;
    } else if (isRateLimitedByGiftcardId) {
      const contextId =
        metadata?.giftcardId || metadata?.params?.giftcardId || '????????';
      groupBy = `giftcard: ${contextId}`;
    }

    const errorMessage = groupBy ? `${errorName} (${groupBy})` : errorName;

    const errorInstance = new Error(errorMessage);

    appSignal.sendError({
      error: errorInstance,
      payload: { error, metadata },
      tags,
    });

    return;
  }

  if (isAxiosError(error) && error.response) {
    const { data = {} } = error.response as { data: Record<string, any> };

    const getErrorInstance = () => {
      if (isZmyleError(data)) return buildErrorInstanceFromZmyleError(data);

      return new Error(error.message);
    };

    const errorInstance = getErrorInstance();

    appSignal.sendError({
      error: errorInstance,
      payload: { error, metadata, response: data },
      tags,
    });

    return;
  }

  if (isZmyleError(error)) {
    const errorInstance = buildErrorInstanceFromZmyleError(error);

    appSignal.sendError({
      error: errorInstance,
      payload: { error, metadata },
      tags,
    });

    return;
  }

  if (error instanceof Error) {
    appSignal.sendError({ error, payload: { error, metadata }, tags });

    return;
  }

  const errorInstance = new Error('Unknown Error');

  const payload = { error: JSON.stringify(error), metadata };

  appSignal.sendError({ error: errorInstance, payload, tags });
};
